import { mutatePurchaseOrderCategories } from "@/api/purchaseOrderCategories/usePurchaseOrderCategoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  PurchaseOrderCategoriesGet200ResponseRowsInner,
  ScmApiPurchaseOrderCategoriesPurchaseOrderCategoryIdPutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
});

export const PurchaseOrderCategoryUpdateForm = (params: {
  formatterProps: PurchaseOrderCategoriesGet200ResponseRowsInner;
}) => {
  const { closeModal } = useModal();
  const { formatterProps } = params;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: updateMutate } = useMutation(
    (params: ScmApiPurchaseOrderCategoriesPurchaseOrderCategoryIdPutRequest) =>
      mutatePurchaseOrderCategories
        .update(params)
        .mutationFn(params as ScmApiPurchaseOrderCategoriesPurchaseOrderCategoryIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrderCategories"]);
        closeModal(formatterProps);
        customAlert(
          `${t("message.editSuccess", { item: `${t("purchaseOrderCategory.category")}` })}`,
          `${t("common.editSuccess")}`,
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("purchaseOrderCategory.name") }),
          `${t("common.editFail", { item: `${t("purchaseOrderCategory.category")}` })}`,
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      name: formatterProps.name,
    },
  });

  const onSubmit = () => {
    try {
      updateMutate({
        purchaseOrderCategoryId: formatterProps.id as number,
        purchaseOrderCategoriesGetRequest: {
          name: form.values.name!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("purchaseOrderCategory.name") }),
        `${t("common.editFail", { item: `${t("purchaseOrderCategory.category")}` })}`,
        "red",
      );
    }
  };

  return (
    <Update.Wrapper>
      <Update.Input
        label={t("purchaseOrderCategory.category")}
        withAsterisk
        placeholder={t("placeholder.input", { item: `${t("purchaseOrderCategory.name")}` })}
        defaultValue={formatterProps?.name}
        {...form.getInputProps("name")}
        error={form.isDirty("name") ? t("common.toSave") : ""}
        styles={{
          input: {
            color: form.isDirty("name") ? "red !important" : "",
            borderColor: form.isDirty("name") ? "red !important" : "",
          },
          error: { fontSize: 11, color: "red" },
        }}
      />
      <Update.ButtonBox>
        <Update.Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Update.Button>
        <Update.Button
          color="blue"
          disabled={validateFormValues({
            pageType: "itemCategory",
            values: form.values,
          })}
          onClick={onSubmit}
        >
          {t("common.edit", { item: `${t("purchaseOrderCategory.category")}` })}
        </Update.Button>
      </Update.ButtonBox>
    </Update.Wrapper>
  );
};
